import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Tab, Tabs } from "react-bootstrap";
import { FaEdit, FaSearch, FaUser, FaUserCheck } from "react-icons/fa";
import Title from "../GenericComponents/Title";
import Loader from "../Loader/Loader";
import {
  get_ApiManager,
  post_ApiManager,
  view_ApiManager,
} from "../ApiManager/ApiManager";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import {
  getAssignedBranchIds,
  getBranchDisplayName,
  getManagerId,
  getUserAssignedBranches,
  getUserBranchRows,
  sortByName,
  normalizeUsersListResponse,
  buildAssignBranchesPayload,
  extractSavedBranchField,
} from "./manageBranchUtils";
import "./manageBranch.css";

function BranchBadgeGrid({
  allBranches,
  assignedIds,
  onToggleBranch,
  disabled = false,
  filter = "",
}) {
  const options = useMemo(() => sortByName(allBranches), [allBranches]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    return options.filter((b) =>
      String(b.name || "").toLowerCase().includes(q),
    );
  }, [options, filter]);

  if (!options.length) {
    return <span className="text-muted small">Loading branches…</span>;
  }

  const interactive = typeof onToggleBranch === "function";

  return (
    <div className="manage-branch-badge-grid" role="list">
      {visible.length === 0 ? (
        <span className="text-muted small">No branches match your search.</span>
      ) : (
        visible.map((branch) => {
          const assigned = assignedIds.has(Number(branch.id));
          const className = `manage-branch-pill${
            assigned ? " is-assigned" : " is-unassigned"
          }`;

          if (!interactive) {
            return (
              <span key={branch.id} className={className} role="listitem">
                {branch.name}
              </span>
            );
          }

          return (
            <button
              key={branch.id}
              type="button"
              className={`${className} manage-branch-pill-btn`}
              aria-pressed={assigned}
              disabled={disabled}
              onClick={() => onToggleBranch(branch)}
            >
              {branch.name}
            </button>
          );
        })
      )}
    </div>
  );
}

function EditUserBranchesModal({
  user,
  open,
  onClose,
  allBranches,
  saving,
  onSave,
}) {
  const [branches, setBranches] = useState([]);
  const [branchesError, setBranchesError] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  const assignedIdSet = useMemo(
    () => new Set(branches.map((b) => Number(b.id))),
    [branches],
  );

  useEffect(() => {
    if (!open || !user) return;
    setBranches(getUserAssignedBranches(user, allBranches));
    setBranchesError("");
    setBranchFilter("");
  }, [open, user, allBranches]);

  const isBranchSelected = (branchId) =>
    branches.some((b) => Number(b.id) === Number(branchId));

  const toggleBranch = (branch) => {
    setBranchesError("");
    if (isBranchSelected(branch.id)) {
      setBranches((prev) =>
        prev.filter((b) => Number(b.id) !== Number(branch.id)),
      );
    } else {
      setBranches((prev) => [...prev, branch]);
    }
  };

  const handleSave = () => {
    if (!branches.length) {
      setBranchesError("Please select at least one branch.");
      return;
    }
    setBranchesError("");
    onSave({ userId: user.id, branches });
  };

  if (!open || !user) return null;

  const selectedCount = branches.length;

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{
          display: "block",
          backgroundColor: "rgba(0,0,0,0.45)",
          zIndex: 1040,
        }}
        onClick={saving ? undefined : onClose}
      />
      <div
        className="modal fade show d-block edit-branch-access-modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="edit-user-branches-title"
        style={{ zIndex: 1050 }}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          style={{ maxWidth: 840, width: "min(92vw, 840px)" }}
        >
          <div
            className="modal-content border-0 rounded-4 shadow d-flex flex-column"
            style={{
              maxHeight: "min(90vh, 720px)",
              overflow: "hidden",
            }}
          >
            <div
              className="modal-header py-3 px-4 flex-shrink-0"
              style={{
                background: "#f7f8fa",
                borderBottom: "1px solid #eef2f6",
              }}
            >
              <div>
                <h5
                  className="modal-title mb-0 fw-bold"
                  id="edit-user-branches-title"
                >
                  Edit branch access
                </h5>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Tap a branch to assign or unassign. Blue = assigned.
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                disabled={saving}
                onClick={onClose}
              />
            </div>

            <div
              className="modal-body px-4 py-4 flex-grow-1"
              style={{ overflowY: "auto", minHeight: 0 }}
            >
              <div
                className="d-flex align-items-center gap-3 rounded-3 p-3 mb-4"
                style={{
                  background: "#eef2ff",
                  border: "1px solid #c7d2fe",
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white flex-shrink-0"
                  style={{ width: 44, height: 44 }}
                  aria-hidden
                >
                  <FaUser />
                </div>
                <div className="min-w-0">
                  <div className="fw-bold text-truncate">{user.name}</div>
                  <div className="text-muted small">
                    {selectedCount} branch{selectedCount === 1 ? "" : "es"}{" "}
                    selected
                  </div>
                </div>
              </div>

              <div className="manage-branch-modal-search mb-3">
                <FaSearch className="manage-branch-search-icon" aria-hidden />
                <input
                  id="edit-branch-filter"
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search branches…"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  aria-label="Search branches"
                />
              </div>
              <BranchBadgeGrid
                allBranches={allBranches}
                assignedIds={assignedIdSet}
                onToggleBranch={toggleBranch}
                disabled={saving}
                filter={branchFilter}
              />
              {branchesError ? (
                <div className="text-danger small mt-3">{branchesError}</div>
              ) : null}
            </div>

            <div
              className="modal-footer py-3 px-4 gap-2 flex-shrink-0"
              style={{
                background: "#f7f8fa",
                borderTop: "1px solid #eef2f6",
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={saving}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary px-4"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden
                    />
                    Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ManageBranch() {
  const permissions = JSON.parse(JSON.stringify(getPermissions()) || "{}");
  const canManage = Number(permissions?.manage_branches) === 1;

  const [activeTab, setActiveTab] = useState("manage");
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingManagerId, setUpdatingManagerId] = useState(null);

  const [allUsers, setAllUsers] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [officeAgents, setOfficeAgents] = useState([]);

  const [userSearch, setUserSearch] = useState("");
  const [managerSearch, setManagerSearch] = useState("");

  const [editUser, setEditUser] = useState(null);

  const loadUsers = useCallback(() => {
    setLoadingUsers(true);
    apiUrl
      .get("getAllUsers", { params: { page: 0, perPage: 0 } })
      .then((res) => {
        setAllUsers(normalizeUsersListResponse(res));
        setLoadingUsers(false);
      })
      .catch(() => {
        setLoadingUsers(false);
      });
  }, []);

  const loadBranches = useCallback(() => {
    get_ApiManager(
      "",
      "getBranches",
      (res) => {
        const rows = [...(res?.data?.data || [])];
        const hasOthers = rows.some((row) => Number(row.id) === 0);
        if (!hasOthers) {
          rows.push({ name: "Others", id: 0 });
        }
        setAllBranches(rows);
      },
      () => setAllBranches([]),
    );
  }, []);

  const loadOfficeAgents = useCallback(() => {
    setLoadingAgents(true);
    view_ApiManager(
      "getagents/1",
      1,
      500,
      (res) => {
        const payload = res?.data?.data;
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        setOfficeAgents(rows);
        setLoadingAgents(false);
      },
      () => {
        setOfficeAgents([]);
        setLoadingAgents(false);
      },
    );
  }, []);

  useEffect(() => {
    if (!canManage) return;
    loadUsers();
    loadBranches();
  }, [canManage, loadUsers, loadBranches]);

  const changeTab = (tab) => {
    setActiveTab(tab);
    if (tab === "manager") {
      loadOfficeAgents();
    }
  };

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const sorted = sortByName(allUsers);
    if (!query) return sorted;
    return sorted.filter((user) => {
      const nameMatch = String(user?.name || "")
        .toLowerCase()
        .includes(query);
      const branchMatch = getUserBranchRows(user, allBranches).some((row) =>
        row.branchName.toLowerCase().includes(query),
      );
      return nameMatch || branchMatch;
    });
  }, [allUsers, allBranches, userSearch]);

  const filteredAgents = useMemo(() => {
    const query = managerSearch.trim().toLowerCase();
    const sorted = [...officeAgents].sort((a, b) =>
      getBranchDisplayName(a).localeCompare(getBranchDisplayName(b)),
    );
    if (!query) return sorted;
    return sorted.filter((agent) =>
      getBranchDisplayName(agent).toLowerCase().includes(query),
    );
  }, [officeAgents, managerSearch]);

  const saveUserBranches = ({ userId, branches }) => {
    const payload = buildAssignBranchesPayload(userId, branches);

    setSaving(true);
    post_ApiManager(
      payload,
      "assignBranches",
      (res) => {
        setSaving(false);
        if (res?.data?.error) {
          Swal.fire("Error", res.data.error, "error");
          return;
        }

        const savedBranchField = extractSavedBranchField(res, branches);

        setAllUsers((prev) =>
          prev.map((user) =>
            Number(user.id) === Number(userId)
              ? {
                  ...user,
                  branch: savedBranchField,
                  selected_branches: savedBranchField,
                  user_branches: res?.data?.data?.user_branches ?? user.user_branches,
                }
              : user,
          ),
        );

        Swal.fire({
          icon: "success",
          title: "Branches updated",
          timer: 2000,
          showConfirmButton: false,
        });
        setEditUser(null);
      },
      () => {
        setSaving(false);
        Swal.fire("Error", "Unable to save branches.", "error");
      },
    );
  };

  const makeBranchManager = (userId, branchId) => {
    if (!userId || !branchId) return;
    setUpdatingManagerId(branchId);
    post_ApiManager(
      { branchid: branchId, userid: userId },
      "makeBranchManager",
      (res) => {
        setUpdatingManagerId(null);
        if (res?.data?.error) {
          Swal.fire("Error", res.data.error, "error");
          return;
        }
        Swal.fire({
          icon: "success",
          title: "Branch manager updated",
          timer: 2000,
          showConfirmButton: false,
        });
        loadOfficeAgents();
      },
      () => {
        setUpdatingManagerId(null);
        Swal.fire("Error", "Unable to assign branch manager.", "error");
      },
    );
  };

  if (!canManage) return null;

  return (
    <>
      <Title title="Manage Branches" />
      <div className="row w-100 pl-3 pr-3 mb-5 manage-branch-page">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => changeTab(k)}
          className="app-tabs ml-0 border rounded mt-0 w-100"
        >
          <Tab eventKey="manage" title="Manage Branch">
            <div className="bg-white border border-top-0 rounded-bottom p-3">
              <p className="text-muted small mb-3 mb-md-2">
                Blue badges are branches assigned to that user. Use Edit to
                change assignments.
              </p>
              <div className="manage-branch-toolbar">
                <div className="manage-branch-search-wrap">
                  <FaSearch
                    className="manage-branch-search-icon"
                    aria-hidden
                  />
                  <input
                    type="search"
                    className="manage-branch-search-input"
                    placeholder="Search by user or assigned branch…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    aria-label="Search users"
                  />
                  {userSearch.trim() ? (
                    <button
                      type="button"
                      className="manage-branch-search-clear"
                      onClick={() => setUserSearch("")}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
                <span className="manage-branch-meta">
                  {filteredUsers.length} user
                  {filteredUsers.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0 manage-branch-user-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 48 }}>#</th>
                      <th style={{ width: "22%" }}>User</th>
                      <th>Branches</th>
                      <th style={{ width: 90 }} className="text-center">
                        Edit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingUsers ? (
                      <tr>
                        <td colSpan={4} className="text-center py-5">
                          <Loader />
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          No users match your search.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user, index) => {
                        const assignedSet = new Set(
                          getAssignedBranchIds(user).map((id) => Number(id)),
                        );
                        return (
                          <tr key={user.id}>
                            <td className="text-muted">{index + 1}</td>
                            <td className="name-text fw-semibold">{user.name}</td>
                            <td>
                              <BranchBadgeGrid
                                allBranches={allBranches}
                                assignedIds={assignedSet}
                              />
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                title="Edit branches"
                                onClick={() => setEditUser(user)}
                              >
                                <FaEdit aria-hidden />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Tab>

          <Tab eventKey="manager" title="Branch Manager">
            <div className="bg-white border border-top-0 rounded-bottom p-3">
              <div className="manage-branch-toolbar">
                <div className="manage-branch-search-wrap">
                  <FaSearch
                    className="manage-branch-search-icon"
                    aria-hidden
                  />
                  <input
                    type="search"
                    className="manage-branch-search-input"
                    placeholder="Search aptitude branch…"
                    value={managerSearch}
                    onChange={(e) => setManagerSearch(e.target.value)}
                    aria-label="Search branches"
                  />
                  {managerSearch.trim() ? (
                    <button
                      type="button"
                      className="manage-branch-search-clear"
                      onClick={() => setManagerSearch("")}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
                <span className="manage-branch-meta">
                  {filteredAgents.length} branch
                  {filteredAgents.length === 1 ? "" : "es"}
                </span>
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="w-50">Aptitude branch</th>
                      <th>Branch manager</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingAgents ? (
                      <tr>
                        <td colSpan={2} className="text-center py-5">
                          <Loader />
                        </td>
                      </tr>
                    ) : filteredAgents.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center py-4 text-muted">
                          No branches found.
                        </td>
                      </tr>
                    ) : (
                      filteredAgents.map((agent) => {
                        const managerId = getManagerId(agent);
                        return (
                          <tr key={agent.id}>
                            <td className="fw-semibold name-text">
                              {getBranchDisplayName(agent)}
                            </td>
                            <td className="py-2">
                              <div className="d-flex align-items-center gap-2">
                                <FaUserCheck
                                  className="text-primary flex-shrink-0"
                                  aria-hidden
                                />
                                <select
                                  className="form-select form-select-sm s-dropdown shadow-sm"
                                  style={{ maxWidth: 280 }}
                                  value={managerId}
                                  disabled={updatingManagerId === agent.id}
                                  onChange={(e) =>
                                    makeBranchManager(e.target.value, agent.id)
                                  }
                                >
                                  <option value="">Select user</option>
                                  {sortByName(allUsers).map((user) => (
                                    <option key={user.id} value={user.id}>
                                      {user.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>

      <EditUserBranchesModal
        user={editUser}
        open={Boolean(editUser)}
        onClose={() => {
          if (!saving) setEditUser(null);
        }}
        allBranches={allBranches}
        saving={saving}
        onSave={saveUserBranches}
      />
    </>
  );
}

export default ManageBranch;
